import React, { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { ApplicationState } from 'store';
import { AcademyState, AcademyLive } from 'store/modules/academy_store/types';
import Loader from 'components/atoms/loader';
import AcademyContentCard from 'components/molecules/academy_content_card';
import { Container } from './styles';

const PER_PAGE = 9;

const AcademyContents: React.FC = () => {
  const history = useHistory();
  const { contents, request } = useSelector<ApplicationState, AcademyState>(
    (s) => s.session.academy,
  );

  const [filter, setFilter] = useState<string>('Todos');
  const [page, setPage] = useState<number>(1);

  const categories = useMemo(() => {
    const set = new Set<string>();
    contents.forEach((c) => c.content_type && set.add(c.content_type));
    return ['Todos', ...Array.from(set)];
  }, [contents]);

  const filtered = useMemo(
    () => (filter === 'Todos' ? contents : contents.filter((c) => c.content_type === filter)),
    [contents, filter],
  );

  const totalPages = Math.max(1, Math.ceil(filtered.length / PER_PAGE));
  const currentPage = Math.min(page, totalPages);
  const pageItems = filtered.slice((currentPage - 1) * PER_PAGE, currentPage * PER_PAGE);

  const selectFilter = (category: string) => {
    setFilter(category);
    setPage(1);
  };

  if (request.contents.loading) {
    return (
      <Container>
        <div className="loading-wrapper">
          <Loader />
        </div>
      </Container>
    );
  }

  if (contents.length === 0) {
    return (
      <Container>
        <div className="empty-state">
          <i className="material-icons">video_library</i>
          <p>Ainda não há conteúdos disponíveis.</p>
        </div>
      </Container>
    );
  }

  return (
    <Container>
      <div className="contents-title">Conteúdos</div>

      {categories.length > 1 && (
        <div className="filters">
          {categories.map((category) => (
            <button
              key={category}
              type="button"
              className={`filter-pill ${filter === category ? 'active' : ''}`}
              onClick={() => selectFilter(category)}
            >
              {category}
            </button>
          ))}
        </div>
      )}

      <div className="row">
        {pageItems.map((content) => (
          <div className="col-md-4 col-sm-6 mb-4" key={content.id}>
            <AcademyContentCard
              content={content}
              onPlay={(item: AcademyLive) => history.push(`/academy/conteudos/${item.id}`)}
            />
          </div>
        ))}
      </div>

      {totalPages > 1 && (
        <div className="pagination-wrapper">
          <button type="button" disabled={currentPage === 1} onClick={() => setPage(1)}>
            «
          </button>
          <button
            type="button"
            disabled={currentPage === 1}
            onClick={() => setPage(currentPage - 1)}
          >
            ‹
          </button>
          {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
            <button
              key={p}
              type="button"
              className={p === currentPage ? 'active' : ''}
              onClick={() => setPage(p)}
            >
              {p}
            </button>
          ))}
          <button
            type="button"
            disabled={currentPage === totalPages}
            onClick={() => setPage(currentPage + 1)}
          >
            ›
          </button>
          <button
            type="button"
            disabled={currentPage === totalPages}
            onClick={() => setPage(totalPages)}
          >
            »
          </button>
        </div>
      )}
    </Container>
  );
};

export default AcademyContents;
